/**
 * Bridge to A4's design system, plus the two primitives A7 still supplies locally.
 *
 * A4's components landed mid build. Pill, Chip, Card, Toggle and Skeleton are re-exported
 * from @/components so there is one implementation of each; DataTable, ConfirmDialog,
 * MoneyText, AgeText and buttonStyle stay here for now because A4's equivalents take a
 * different shape and swapping them is an integration step, not a build one. Every one of
 * these is marked TEMP and none of them writes a colour, size, radius or z-index as a
 * literal: they read the tokens in theme/tokens.css, so the swap is visually neutral.
 */
import type { CSSProperties, ReactNode } from 'react';
export { Pill, Chip } from '@/components/Primitives';
export type { Tone as PillTone } from '@/components/Primitives';
export { Card } from '@/components/Card';
export { Toggle } from '@/components/Toggle';
export { Skeleton } from '@/components/Skeleton';

export interface Column<T> {
  id: string;
  header: string;
  priority: 1 | 2 | 3 | 4 | 5;
  align?: 'left' | 'right';
  mono?: boolean;
  render: (row: T) => ReactNode;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  selection,
  emptyState,
  caption,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  selection?: { selected: Set<string>; onToggle: (key: string) => void };
  emptyState: ReactNode;
  caption?: string;
}) {
  if (rows.length === 0) {
    return <div style={{ padding: 'var(--space-6)', color: 'var(--text-muted)' }}>{emptyState}</div>;
  }
  return (
    <div style={{ overflowX: 'auto' }} data-allow-hscroll>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--type-body-dense)' }}>
        {caption ? <caption style={{ captionSide: 'bottom', color: 'var(--text-faint)' }}>{caption}</caption> : null}
        <thead>
          <tr>
            {selection ? <th style={{ width: 32 }} /> : null}
            {columns.map((column) => (
              <th
                key={column.id}
                className="caps-label"
                style={{
                  textAlign: column.align ?? 'left',
                  padding: 'var(--cell-padding-y) var(--cell-padding-x)',
                  borderBottom: 'var(--border-width) solid var(--border)',
                  color: 'var(--text-faint)',
                }}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const key = rowKey(row);
            return (
              <tr
                key={key}
                data-row-key={key}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                style={{
                  borderBottom: 'var(--border-width) solid var(--border)',
                  cursor: onRowClick ? 'pointer' : 'default',
                }}
              >
                {selection ? (
                  <td style={{ padding: 'var(--cell-padding-y) var(--cell-padding-x)' }}>
                    <input
                      type="checkbox"
                      aria-label={`Select ${key}`}
                      checked={selection.selected.has(key)}
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => selection.onToggle(key)}
                    />
                  </td>
                ) : null}
                {columns.map((column) => (
                  <td
                    key={column.id}
                    {...(column.mono ? { 'data-mono': 'true' } : {})}
                    style={{
                      textAlign: column.align ?? 'left',
                      padding: 'var(--cell-padding-y) var(--cell-padding-x)',
                    }}
                  >
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function ConfirmDialog({
  open,
  title,
  body,
  effects,
  confirmLabel,
  onConfirm,
  onCancel,
  reasonRequired,
  busy,
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  effects: string[];
  confirmLabel: string;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
  reasonRequired?: boolean;
  busy?: boolean;
}) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 'var(--z-dialog)' as unknown as number,
        display: 'grid',
        placeItems: 'center',
        background: 'var(--shadow-scrim)',
      }}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          const reason = new FormData(event.currentTarget).get('reason');
          onConfirm(typeof reason === 'string' ? reason : '');
        }}
        style={{
          background: 'var(--surface-1)',
          border: 'var(--border-width) solid var(--copper)',
          borderRadius: 'var(--radius-card)',
          padding: 'var(--card-padding)',
          maxWidth: 520,
          width: '90vw',
        }}
      >
        <h2 style={{ marginTop: 0, fontSize: 'var(--type-heading-3)' }}>{title}</h2>
        <div style={{ color: 'var(--text-muted)' }}>{body}</div>
        <ul style={{ color: 'var(--text-muted)', fontSize: 'var(--type-meta)' }}>
          {effects.map((effect) => (
            <li key={effect}>{effect}</li>
          ))}
        </ul>
        {reasonRequired ? (
          <label style={{ display: 'block', marginBottom: 'var(--space-3)' }}>
            <span className="caps-label">Reason</span>
            <input
              name="reason"
              required
              minLength={3}
              style={{
                display: 'block',
                width: '100%',
                padding: 'var(--space-2)',
                borderRadius: 'var(--radius-input)',
                border: 'var(--border-width) solid var(--border)',
                background: 'var(--surface-2)',
                color: 'var(--text)',
              }}
            />
          </label>
        ) : null}
        <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onCancel} style={buttonStyle('ghost')}>
            Cancel
          </button>
          <button type="submit" disabled={busy} style={buttonStyle('primary')}>
            {confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

export function buttonStyle(kind: 'primary' | 'ghost' | 'danger' = 'ghost'): CSSProperties {
  return {
    minHeight: 'var(--tap-target-min)',
    padding: 'var(--space-2) var(--space-4)',
    borderRadius: 'var(--radius-input)',
    border: 'var(--border-width) solid var(--border)',
    background:
      kind === 'primary' ? 'var(--burgundy)' : kind === 'danger' ? 'var(--crit-bg)' : 'var(--surface-2)',
    color: kind === 'danger' ? 'var(--crit)' : 'var(--text)',
    fontSize: 'var(--type-body-dense)',
    cursor: 'pointer',
  };
}

export function MoneyText({ cents, tone }: { cents: number | null | undefined; tone?: string }) {
  if (cents === null || cents === undefined) {
    return <span style={{ color: 'var(--text-faint)' }}>no data</span>;
  }
  const negative = cents < 0;
  const text = `${negative ? '-' : ''}$${(Math.abs(cents) / 100).toFixed(2)}`;
  return (
    <span data-mono="true" style={{ color: tone ?? (negative ? 'var(--crit)' : 'var(--text)') }}>
      {text}
    </span>
  );
}

export function AgeText({ at }: { at: string | null }) {
  if (!at) return <span style={{ color: 'var(--text-faint)' }}>never</span>;
  const ms = Date.now() - Date.parse(at);
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor(ms / 3_600_000);
  const label = days >= 1 ? `${days}d` : hours >= 1 ? `${hours}h` : `${Math.max(0, Math.floor(ms / 60_000))}m`;
  return <span style={{ color: 'var(--text-muted)' }}>{label}</span>;
}

